import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Users, TrendingUp, Edit, Save, X, ChevronDown, ChevronUp, Settings } from 'lucide-react';

interface Parameter {
  name: string;
  weight: number;
  enabled: boolean;
  scale: string;
}

interface ScoringAgentParametersProps {
  agentParameters: Parameter[];
  botParameters: Parameter[];
  onSave: (data: { agentParameters: Parameter[]; botParameters: Parameter[] }) => void;
}

const ScoringAgentParameters: React.FC<ScoringAgentParametersProps> = ({
  agentParameters,
  botParameters,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [formData, setFormData] = useState({
    agentParameters: [...agentParameters],
    botParameters: [...botParameters]
  });

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      agentParameters: [...agentParameters],
      botParameters: [...botParameters]
    });
    setIsEditing(false);
  };

  const updateParameter = (type: 'agentParameters' | 'botParameters', index: number, field: keyof Parameter, value: any) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map((param, i) => 
        i === index ? { ...param, [field]: value } : param
      )
    }));
  };

  const redistributeWeights = (type: 'agentParameters' | 'botParameters') => {
    const parameters = formData[type];
    const enabledParams = parameters.filter(p => p.enabled);
    const totalEnabled = enabledParams.length;
    
    if (totalEnabled === 0) return;
    
    const equalWeight = Math.round((100 / totalEnabled) * 10) / 10;
    let remaining = 100;
    
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map((param, index) => {
        if (!param.enabled) return { ...param, weight: 0 };
        
        const enabledIndex = enabledParams.findIndex(p => p.name === param.name);
        if (enabledIndex === totalEnabled - 1) {
          // Last enabled parameter gets remaining weight
          return { ...param, weight: Math.round(remaining * 10) / 10 };
        } else {
          remaining -= equalWeight;
          return { ...param, weight: equalWeight };
        }
      })
    }));
  };

  const toggleParameter = (type: 'agentParameters' | 'botParameters', index: number) => {
    updateParameter(type, index, 'enabled', !formData[type][index].enabled);
    // Redistribute weights after toggling
    setTimeout(() => redistributeWeights(type), 0);
  };

  const calculateTotal = (parameters: Parameter[]) => {
    return parameters.filter(p => p.enabled).reduce((sum, p) => sum + p.weight, 0);
  };

  const ParameterSection = ({ 
    title, 
    icon: Icon, 
    parameters, 
    type 
  }: { 
    title: string; 
    icon: any; 
    parameters: Parameter[]; 
    type: 'agentParameters' | 'botParameters' 
  }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          {title}
          {isEditing && (
            <Badge variant="outline" className="ml-auto">
              Total: {calculateTotal(formData[type])}%
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {parameters.map((param, index) => (
            <div key={index} className={`flex items-center justify-between p-3 border rounded-lg ${isEditing ? 'bg-muted/30' : ''}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{param.name}</h4>
                  {!isEditing ? (
                    <Badge variant={param.enabled ? 'default' : 'secondary'} className="text-xs">
                      {param.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={formData[type][index].enabled}
                        onCheckedChange={() => toggleParameter(type, index)}
                      />
                      <Label className="text-xs">Enabled</Label>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Scale: {param.scale}</p>
              </div>
              <div className="text-right">
                {!isEditing ? (
                  <p className="font-medium">{param.weight}%</p>
                ) : (
                  <div className="w-24">
                    <Input
                      type="number"
                      value={formData[type][index].weight}
                      onChange={(e) => updateParameter(type, index, 'weight', parseFloat(e.target.value) || 0)}
                      min={0}
                      max={100}
                      step={0.1}
                      className="text-right"
                      disabled={!formData[type][index].enabled}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isEditing && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex justify-between items-center text-sm">
                <span>Total Weight:</span>
                <span className={`font-medium ${Math.abs(calculateTotal(formData[type]) - 100) < 0.1 ? 'text-green-600' : 'text-red-600'}`}>
                  {calculateTotal(formData[type])}%
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => redistributeWeights(type)}
                className="mt-2 w-full"
              >
                Auto-distribute Weights
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="hover:bg-muted/30 transition-colors">
            <CardTitle className="flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent/50 rounded-lg">
                  <Settings className="h-5 w-5 text-accent-foreground" />
                </div>
                Scoring Parameters
              </div>
              <div className="flex items-center gap-2">
                {!isCollapsed && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                {!isCollapsed && isEditing && (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave}>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-6">
            {agentParameters.length > 0 && (
              <ParameterSection
                title="Agent Scoring Parameters"
                icon={Users}
                parameters={agentParameters}
                type="agentParameters"
              />
            )}

            {botParameters.length > 0 && (
              <ParameterSection
                title="Bot Scoring Parameters"
                icon={TrendingUp}
                parameters={botParameters}
                type="botParameters"
              />
            )}

            {isEditing && (agentParameters.length > 0 || botParameters.length > 0) && (
              <div className="p-4 bg-muted/30 border border-border rounded-lg">
                <h4 className="font-medium text-foreground mb-2">Weight Distribution Guidelines</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Each parameter group should total 100%</li>
                  <li>• Disabled parameters automatically get 0% weight</li>
                  <li>• Use "Auto-distribute Weights" to evenly distribute across enabled parameters</li>
                  <li>• Weight distribution affects scoring calculation accuracy</li>
                </ul>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ScoringAgentParameters;